
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Route, NavigationGuardNext } from 'vue-router';

import store from '@/store';

// Components
import FondoDiagonal from '@/componentes/UI/FondoDiagonal.vue';
import ProgresoPasos from '@/componentes/ProgresoPasos.vue';
import FormCard from '@/componentes/UI/FormCard.vue';
import FormInput from '@/componentes/UI/FormInput.vue';
import CustomCheckbox from '@/componentes/UI/CustomCheckbox.vue';
import InfoAlert from '@/componentes/UI/InfoAlert.vue';
import ErrorAlert from '@/componentes/UI/ErrorAlert.vue';
import ActionButton from '@/componentes/UI/ActionButton.vue';
import SectionHeader from '@/componentes/UI/SectionHeader.vue';

// API
import { DatosContactoI, getDatosContacto, patchDatosContacto } from '@/assets/api/matricula';
import { RegionI, ComunaI, getRegiones, PaisI, getPaises } from '@/assets/api/general';
import HeaderPrincipal from "@/componentes/UI/HeaderPrincipal.vue";

// Types
interface FormState {
  hasSocialName: boolean;
  customGender: string;
  selectedRegion: RegionI | null;
  selectedComuna: ComunaI | null;
  selectedNationality: PaisI | null;
  confirmEmail: string;
  isLoading: boolean;
  error: string;
}

@Component({
  components: {
    HeaderPrincipal,
    FondoDiagonal,
    ProgresoPasos,
    FormCard,
    FormInput,
    CustomCheckbox,
    InfoAlert,
    ErrorAlert,
    ActionButton,
    SectionHeader,
  },
})
export default class PerfilEstudianteEduContinua extends Vue {
  // Data properties
  datosContacto: DatosContactoI = {} as DatosContactoI;
  regiones: RegionI[] = [];
  paises: PaisI[] = [];
  generos = ['masculino', 'femenino', 'otro'];

  form: FormState = {
    hasSocialName: false,
    customGender: '',
    selectedRegion: null,
    selectedComuna: null,
    selectedNationality: null,
    confirmEmail: '',
    isLoading: false,
    error: '',
  };

  // Computed properties for validation errors
  get emailValidationError(): string {
    if (!this.form.confirmEmail && this.datosContacto.mail_secundario) {
      return 'Por favor confirma tu correo electrónico';
    }
    if (this.form.confirmEmail && this.datosContacto.mail_secundario &&
      this.form.confirmEmail !== this.datosContacto.mail_secundario) {
      return 'Los correos electrónicos no coinciden';
    }
    return '';
  }

  get emergencyPhoneError(): string {
    const hasPhone = !!(
      this.datosContacto.emergencia_telefono_celular ||
      this.datosContacto.emergencia_telefono_laboral ||
      this.datosContacto.emergencia_telefono_fijo
    );

    if (!hasPhone && (this.datosContacto.emergencia_nombre || this.datosContacto.emergencia_parentezco)) {
      return 'Por favor ingresa al menos un teléfono de contacto de emergencia';
    }
    return '';
  }

  get planColor(): string {
    // Convert CSS variable to actual hex color
    const colorVar = this.$store.getters.colorPlan;

    // If it's a CSS variable, return the actual color value
    if (colorVar.includes('var(')) {
      // Map of CSS variables to actual colors
      const colorMap: Record<string, string> = {
        'var(--azul-institucional)': '#375282',
        'var(--verde-institucional)': '#40b99d',
        'var(--celeste-institucional)': '#56c0e8',
        'var(--azul-enfermeria)': '#273470',
        'var(--rojo-obstetricia)': '#AB2326',
        'var(--verde-agronomia)': '#639244',
        'var(--verde-forestal)': '#136E49',
        'var(--naranjo-industrial)': '#F06A26',
        'var(--amarillo-social)': '#F9b81C',
        'var(--celeste-psicologia)': '#00ACB4',
        'var(--rosado-informatica)': '#D60B51',
        'var(--verde-terapia-ocupacional)': '#336666',
        'var(--azul-ing-comercial)': '#006699',
        'var(--naranjo)': '#f27c08'
      };

      return colorMap[colorVar] || '#375282'; // fallback to azul-institucional
    }

    return colorVar; // If it's already a hex color, return as is
  }

  // Route guard
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext<PerfilEstudianteEduContinua>) {
    const promesaDatos = getDatosContacto(store.getters.idEstudiante);
    const promesaRegiones = getRegiones();
    const promesaPaises = getPaises();

    Promise.all([promesaDatos, promesaRegiones, promesaPaises])
      .then(([datosContacto, regiones, paises]) => {
        next((vm) => {
          vm.initializeComponent(datosContacto, regiones, paises);
        });
      })
      .catch(() => {
        next({ name: store.getters.linkInicio });
      });
  }

  // Methods
  private initializeComponent(
    datosContacto: DatosContactoI,
    regiones: RegionI[],
    paises: PaisI[],
  ): void {
    this.datosContacto = datosContacto;
    this.regiones = regiones;
    this.paises = paises;

    this.setupInitialFormState();
  }

  private setupInitialFormState(): void {
    // Set region and comuna
    this.regiones.forEach((region) => {
      region.comunas.forEach((comuna) => {
        if (comuna.id === this.datosContacto.direccion_comuna) {
          this.form.selectedComuna = comuna;
          this.form.selectedRegion = region;
        }
      });
    });

    // Set nationality
    this.paises.forEach((pais) => {
      if (pais.id === this.datosContacto.nacionalidad) {
        this.form.selectedNationality = pais;
      }
    });

    // Handle custom gender
    if (this.datosContacto.genero && !this.generos.includes(this.datosContacto.genero)) {
      this.form.customGender = this.datosContacto.genero;
      this.datosContacto.genero = 'otro';
    }

    // Set social name checkbox
    this.form.hasSocialName = Boolean(this.datosContacto.nombre_social);

    // Set confirm email for existing data
    if (store.getters.etapaEstudiante > 3) {
      this.form.confirmEmail = this.datosContacto.mail_secundario;
    }
  }

  // Event handlers
  handleGenderChange(): void {
    if (this.datosContacto.genero !== 'otro') {
      this.form.customGender = '';
    }
  }

  handleRegionChange(): void {
    this.form.selectedComuna = null;
  }

  async handleSubmit(): Promise<void> {
    if (!this.validateForm()) return;

    this.form.isLoading = true;
    this.form.error = '';

    try {
      this.prepareSubmissionData();
      const response = await patchDatosContacto(store.getters.idEstudiante, this.datosContacto);

      store.commit('actualizarNombre', response.nombre_social);
      store.commit('actualizarEtapa', 2);
      // Force navigation after store update
      await this.$nextTick();
      await this.$router.push({ name: 'pago matricula' });

    } catch (error) {
      this.form.error = 'Ha ocurrido un error. Por favor intenta nuevamente o contacta al encargado de matrícula.';
    } finally {
      this.form.isLoading = false;
    }
  }

  private prepareSubmissionData(): void {
    // Set IDs from selected objects
    if (this.form.selectedComuna) {
      this.datosContacto.direccion_comuna = this.form.selectedComuna.id;
    }
    if (this.form.selectedNationality) {
      this.datosContacto.nacionalidad = this.form.selectedNationality.id;
    }

    // Handle custom gender
    if (this.datosContacto.genero === 'otro') {
      this.datosContacto.genero = this.form.customGender;
    }
  }

  // Validation methods
  private validateForm(): boolean {
    const validations = [
      this.validateComuna(),
      this.validateNationality(),
      this.validateEmailConfirmation(),
      this.validateEmergencyPhone(),
    ];

    return validations.every(Boolean);
  }

  private validateComuna(): boolean {
    if (!this.form.selectedComuna) {
      this.form.error = 'Por favor selecciona tu comuna';
      this.scrollToTop();
      return false;
    }
    return true;
  }

  private validateNationality(): boolean {
    if (!this.form.selectedNationality) {
      this.form.error = 'Por favor selecciona tu nacionalidad';
      this.scrollToTop();
      return false;
    }
    return true;
  }

  private validateEmailConfirmation(): boolean {
    if (!this.form.confirmEmail) {
      this.form.error = 'Por favor confirma tu correo electrónico';
      this.scrollToTop();
      return false;
    }
    if (this.form.confirmEmail !== this.datosContacto.mail_secundario) {
      this.form.error = 'Los correos electrónicos no coinciden';
      this.scrollToTop();
      return false;
    }
    return true;
  }

  private validateEmergencyPhone(): boolean {
    const hasPhone = !!(
      this.datosContacto.emergencia_telefono_celular ||
      this.datosContacto.emergencia_telefono_laboral ||
      this.datosContacto.emergencia_telefono_fijo
    );

    if (!hasPhone) {
      this.form.error = 'Por favor ingresa al menos un teléfono de contacto de emergencia';
      return false;
    }
    return true;
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // Watchers for real-time validation and error clearing
  @Watch('form.confirmEmail')
  @Watch('datosContacto.mail_secundario')
  onEmailChange(): void {
    if (this.form.error.includes('correo')) {
      this.form.error = '';
    }
  }

  @Watch('form.selectedComuna')
  onComunaChange(): void {
    if (this.form.error.includes('comuna')) {
      this.form.error = '';
    }
  }

  @Watch('form.selectedNationality')
  onNationalityChange(): void {
    if (this.form.error.includes('nacionalidad')) {
      this.form.error = '';
    }
  }

  @Watch('datosContacto.emergencia_telefono_celular')
  @Watch('datosContacto.emergencia_telefono_laboral')
  @Watch('datosContacto.emergencia_telefono_fijo')
  onEmergencyPhoneChange(): void {
    if (this.form.error.includes('teléfono')) {
      this.form.error = '';
    }
  }

  // Legacy watchers for compatibility (commented as they're replaced by computed properties)
  /*
  @Watch('check_nombre_social')
  validarCheck() {
    this.isEnabled = !this.check_nombre_social;
  }

  @Watch('comuna')
  validarComuna() {
    const input = (this.$refs.comuna as Vue).$refs.search as HTMLFormElement;
    // Validation logic moved to computed properties and form validation
  }

  @Watch('nacionalidad')
  validarNacionalidad() {
    const input = (this.$refs.nacionalidad as Vue).$refs.search as HTMLFormElement;
    // Validation logic moved to computed properties and form validation
  }

  @Watch('datosContacto.mail_secundario')
  @Watch('confirmarCorreo')
  validarConfirmarCorreo() {
    const input = this.$refs.confirmarCorreo as HTMLFormElement;
    // Validation logic moved to computed properties and form validation
  }

  @Watch('datosContacto.emergencia_telefono_celular')
  @Watch('datosContacto.emergencia_telefono_laboral')
  @Watch('datosContacto.emergencia_telefono_fijo')
  validarEmergenciaTelefono() {
    const input = this.$refs.emergencia_telefono_celular as HTMLFormElement;
    // Validation logic moved to computed properties and form validation
  }
  */
}
